import { enroll } from '@api/selfServiceServices';
import {
  type AppError,
  FeatureFlagsProvider,
  GlobalEventSources,
  useEventListener,
} from '@imprivata-cloud/core';
import {
  Button,
  NotificationSystem,
  notificationFromAppEvent,
  useNotificationChannel,
} from '@imprivata-cloud/design-system';
import {
  DefaultFaceCaptureBanner,
  FaceCaptureEnroll,
  FaceProcessingStatus,
} from '@imprivata-cloud/enroll';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SelfServiceLandingPageDataResponse, UserInfo } from '@/api/types';

interface EnrollNotificationPayload {
  online: boolean;
  status: FaceProcessingStatus;
  /**
   * The attempt is terminal: the captured face is already enrolled to another user,
   * so re-capturing it can only be rejected again. The banner's own copy sends the
   * user to the help desk and the modal footer's Cancel is the way out, so no retry
   * is offered. Obeyed rather than derived from `!online`, which is the other
   * reason an attempt cannot be retried right now — and that one recovers.
   */
  cancellable: boolean;
}

interface EnrollFlowProps {
  userInfo: UserInfo | undefined;
  data: SelfServiceLandingPageDataResponse | undefined;
  onSuccessfulFaceEnrollment: () => Promise<undefined | AppError>;
}
export const EnrollFlow = ({ userInfo, onSuccessfulFaceEnrollment, data }: EnrollFlowProps) => {
  const { t } = useTranslation();
  const [key, setKey] = useState(0);
  const notificationChannel = useNotificationChannel();

  const forceUpdateComponent = useCallback(() => {
    setKey(key => key + 1);
  }, []);

  const translate = useCallback((i18nKey: string) => t(i18nKey), [t]);

  useEventListener(event => notificationChannel.publish(notificationFromAppEvent(event)));

  return (
    <FeatureFlagsProvider flags={{ fitVideoFrame: true }}>
      <NotificationSystem
        subscribe={notificationChannel.subscribe}
        renderCustomBanner={notification => {
          const payload = notification?.payload as EnrollNotificationPayload;
          return (
            <DefaultFaceCaptureBanner
              status={notification?.level ? notification.level : 'info'}
              title={notification?.title}
              message={notification?.message}
              actions={
                payload?.status === FaceProcessingStatus.FAILED && !payload?.cancellable ? (
                  <Button variant="link" disabled={!payload?.online} onClick={forceUpdateComponent}>
                    {t('common.try-again')}
                  </Button>
                ) : undefined
              }
            />
          );
        }}
      />
      <GlobalEventSources translate={translate} />
      <FaceCaptureEnroll.Root
        key={key}
        username={data?.userInfo.username || userInfo?.userUpn || userInfo?.email || ''}
        reset={forceUpdateComponent}
        enroll={enroll}
        handleEnrollComplete={onSuccessfulFaceEnrollment}
        isSuccessHandlerAutomatic={true}
      >
        <FaceCaptureEnroll.Overlay>
          <FaceCaptureEnroll.Camera />
        </FaceCaptureEnroll.Overlay>
      </FaceCaptureEnroll.Root>
    </FeatureFlagsProvider>
  );
};
